import { Controller } from 'stimulus';

/**
 * Generic inline toggle for boolean fields in tables.
 * Uses Bootstrap form-switch styling.
 *
 * Handles change events in capture phase to prevent row-level
 * handlers from intercepting the toggle action.
 *
 * Values:
 *   url: The endpoint to PATCH
 *   resource: The resource key (e.g., "vip", "user")
 *   field: The field name to update
 *   checkedLabel / uncheckedLabel: optional labels used to keep
 *     Tabulator data-value / data-sort-value in sync after a toggle
 */
export default class extends Controller {
  static values = {
    url: String,
    resource: String,
    field: String,
    checkedLabel: String,
    uncheckedLabel: String,
  };

  static targets = ['checkbox'];

  connect() {
    this.element.addEventListener('change', this.handleChange, true);
  }

  disconnect() {
    this.element.removeEventListener('change', this.handleChange, true);
  }

  handleChange = (event) => {
    // Stop propagation to prevent row-level handlers
    event.stopPropagation();
    event.stopImmediatePropagation();

    const isChecked = this.checkboxTarget.checked;

    if (this.urlValue) {
      this.updateServer(isChecked);
    }
  };

  async updateServer(newValue) {
    this.checkboxTarget.disabled = true;

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        body: JSON.stringify({
          [this.resourceValue]: { [this.fieldValue]: newValue },
        }),
      });

      // response.redirected catches an expired session / Pundit redirect that
      // fetch silently follows to a 200 HTML page — without it the toggle would
      // report success and desync from the unsaved server state.
      if (!response.ok || response.redirected) {
        this.checkboxTarget.checked = !newValue;
        window.sendError?.(
          `Remote checkbox toggle failed (${response.status})`,
          new Error(await response.text())
        );
      } else {
        this.syncTabulatorSortValue(newValue);
      }
    } catch (error) {
      this.checkboxTarget.checked = !newValue;
      window.sendError?.('Remote checkbox toggle failed', error);
    } finally {
      this.checkboxTarget.disabled = false;
    }
  }

  syncTabulatorSortValue(newValue) {
    if (!this.hasCheckedLabelValue || !this.hasUncheckedLabelValue) return;

    const label = newValue ? this.checkedLabelValue : this.uncheckedLabelValue;
    const cellEl = this.element.closest('.tabulator-cell, td');
    if (!cellEl) return;

    if (cellEl.dataset.value !== undefined) {
      cellEl.dataset.value = label;
    }

    const sortWrapper = cellEl.querySelector('[data-sort-value]');
    if (sortWrapper) {
      sortWrapper.setAttribute('data-sort-value', label);
    }

    this.updateTabulatorRowData(cellEl, label, newValue);
  }

  updateTabulatorRowData(cellEl, label, newValue) {
    const field = cellEl.getAttribute('tabulator-field');
    const rowEl = cellEl.closest('.tabulator-row');
    const tableEl = this.element.closest(
      '[data-controller~="tabulator-table"]'
    );
    if (!field || !rowEl || !tableEl) return;

    const tableController =
      this.application.getControllerForElementAndIdentifier(
        tableEl,
        'tabulator-table'
      );
    const tabulator = tableController?.tabulator;
    if (!tabulator) return;

    const row = tabulator.getRow(rowEl);
    if (!row) return;

    const current = row.getData()[field];
    if (typeof current !== 'string') return;

    let updated = current.includes('data-sort-value=')
      ? current.replace(/data-sort-value="[^"]*"/, `data-sort-value="${label}"`)
      : current;
    updated = syncCheckedAttribute(updated, newValue);

    // Mutate row data in place. Calling `row.update()` redraws the cell from
    // the stored HTML and would restore a stale checkbox `checked` state —
    // the toggle flashes then snaps back until a second click.
    row.getData()[field] = updated;
  }
}

function syncCheckedAttribute(html, checked) {
  return html.replace(/<input\b([^>]*)>/gi, (_match, attrs) => {
    const withoutChecked = attrs.replace(
      /\s+checked(=("[^"]*"|'[^']*'|[^\s>]+))?/gi,
      ''
    );
    return checked
      ? `<input${withoutChecked} checked="checked">`
      : `<input${withoutChecked}>`;
  });
}
